import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Label, Textarea } from '@1trading/react';
import { useState } from 'react';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { autoAssignParkedDeposits } from '@/services/api/parked-deposits';

interface AutoAssignDialogProps {
  transactionIds: string[];
  /** Fired right after a successful run — the page clears the table selection */
  onRunCompleted: () => void;
  /** `didRun` true → the page refetches the list; closing without running does not */
  onClose: (didRun: boolean) => void;
}

export const AutoAssignDialog = ({ transactionIds, onRunCompleted, onClose }: AutoAssignDialogProps) => {
  const [resultsJson, setResultsJson] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasRun = resultsJson !== null;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose(hasRun);
  };

  // Deliberate fix: legacy called autoAssign('') straight from this
  // button, so the audit record's comment was always empty — unlike every other
  // mutating action on this page. Route the confirm through the shared
  // audit-justification dialog and send the justification as `comment`.
  const handleConfirm = () =>
    confirmAction({
      title: 'Confirm Auto Assign Parked Deposits',
      description: 'Proceeding with this action will attempt to auto assign the selected parked deposits. Your action here will be logged and will be auditable.',
      actionLabel: 'Auto Assign Parked Deposits',
      onConfirm: async (justification) => {
        setIsSubmitting(true);
        try {
          const response = await autoAssignParkedDeposits({ processParkedDepositIds: transactionIds, comment: justification });
          // Raw JSON on purpose — operators paste it into tickets.
          // JSON.stringify(undefined) yields undefined at runtime despite the lib type.
          const serialized: string | undefined = JSON.stringify(response.results);
          setResultsJson(serialized ?? '');
          onRunCompleted();
        } finally {
          setIsSubmitting(false);
        }
      },
    });

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : handleClose())} open>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>Auto assign the following Parked Deposits?</DialogTitle>
        </DialogHeader>
        <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
          <p className="text-sm">Are you sure you wish to attempt to auto assign the following parked Deposits? The results will be displayed in this dialog</p>
          <div className="flex flex-col gap-1 font-mono text-xs">
            {transactionIds.map((transactionId) => (
              <span key={transactionId}>{transactionId}</span>
            ))}
          </div>
          {hasRun ? (
            <div className="flex flex-col gap-2">
              <Label htmlFor="auto-assign-results">Results</Label>
              <Textarea className="min-h-32 font-mono text-xs" id="auto-assign-results" readOnly value={resultsJson} />
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button disabled={isSubmitting} onClick={handleClose} type="button" variant="destructive">
            Close
          </Button>
          {hasRun ? null : (
            <Button disabled={isSubmitting} onClick={handleConfirm} type="button" variant="primary">
              Confirm
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
AutoAssignDialog.displayName = 'AutoAssignDialog';
